import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CaseActivityCard from "@_components/CaseActivityCard/CaseActivityCard";
import styles from "./CaseActivitySection.module.css";
import { caseActivityActions } from "@_actions/caseActivity.actions";
import { invoiceTemplateActions } from "@_actions/invoiceTemplate.actions";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import TextfieldInput from "@_components/TextfieldInput/TextfieldInput";
import {
  Button,
  CircularProgress,
  DialogActions,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import CallbackLogItem from "@_components/CallbackLogItem/CallbackLogItem";
import { caseActions } from "@_actions/case.actions";
import { caseConstants } from "@_constants/case.constants";
import AreYouSurePopup from "@_components/popups/AreYouSurePopup/AreYouSurePopup";
import { catchError } from "@_utils/helpers";
import AddressPopup from "@_components/popups/AddressPopup/AddressPopup";
import InfoPopup from "@_components/popups/InfoPopup/InfoPopup";
import { templateService } from "@_services/template.service";
import { customerActions } from "@_actions/customer.actions";
import CaseActivityFilter from "@_components/CaseActivityFilter/CaseActivityFilter";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";

const ACTIVITY_FILTER_STORAGE_KEY = "caseActivityEnabledCategories";

const DEFAULT_ENABLED_CATEGORIES = {
  [caseConstants.CASE_ACTIVITY_CATEGORY_COMMUNICATION]: true,
  [caseConstants.CASE_ACTIVITY_CATEGORY_USER_ACTION]: true,
  [caseConstants.CASE_ACTIVITY_CATEGORY_WEBSITE_OWNER_ACTION]: true,
  [caseConstants.CASE_ACTIVITY_CATEGORY_OTHER]: true,
};

const loadEnabledCategories = () => {
  const stored = localStorage.getItem(ACTIVITY_FILTER_STORAGE_KEY);
  return stored ? JSON.parse(stored) : DEFAULT_ENABLED_CATEGORIES;
};

const saveEnabledCategories = (categories) => {
  localStorage.setItem(ACTIVITY_FILTER_STORAGE_KEY, JSON.stringify(categories));
};

function CaseActivitySection(props) {
  const { data } = props;
  const caseActivityReducer = useSelector((state) => {
    return state.caseActivityReducer;
  });
  const caseReducer = useSelector((state) => state.caseReducer);

  const dispatch = useDispatch();
  const addNoteState =
    caseActivityReducer &&
    caseActivityReducer.status &&
    caseActivityReducer.status.CASEACTIVITY_ADD === "success";
  const deleteNoteState =
    caseActivityReducer &&
    caseActivityReducer.status &&
    caseActivityReducer.status.CASEACTIVITY_DELETE === "success";

  const [addNoteDialogOpen, setAddNoteDialogOpen] = useState(false);
  const [openAddressPopup, setOpenAddressPopup] = useState(false);
  const [noteInput, setNoteInput] = useState(null);
  const [addNoteErrorActive, setAddNoteErrorActive] = useState(false);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [showFutureSection, setShowFutureSection] = useState(false);
  // Derived from `data` so the label stays in sync after pause/resume
  // without needing a manual reload.
  const isAutomationPaused =
    !!data &&
    (data.num_scheduled_emails_paused > 0 ||
      data.num_scheduled_emails_failed > 0 ||
      data.num_triggered_emails_failed > 0);
  const [resumeAutomationPopup, setResumeAutomationPopup] = useState(false);
  const [sentFailedEmailType, setSentFailedEmailType] = useState("");
  const [openInfoPopup, setOpenInfoPopup] = useState(false);
  const [infoPopupText, setInfoPopupText] = useState("");
  const [infoPopupTitle, setInfoPopupTitle] = useState("");
  const [emailTemplates, setEmailTemplates] = useState([]);
  const { snackbarProps, showSnackbarSuccess, showSnackbarError } = useSnackbar();

  const [scheduledActivities, setScheduledActivities] = useState([]);

  const [enabledCategories, setEnabledCategories] = useState(
    loadEnabledCategories,
  );

  const toggleCategory = (category) => {
    const nextEnabledCategories = {
      ...enabledCategories,
      [category]: !enabledCategories[category],
    };
    setEnabledCategories(nextEnabledCategories);
    saveEnabledCategories(nextEnabledCategories);
  };

  /**
   * Scrolls the case activity view to the top and fetches invoice templates.
   *
   * @dependencies
   * - dispatch
   *
   * @triggers
   * Once on mount.
   *
   * @sideEffects
   * - `window.scrollTo`, `invoiceTemplateActions.getInvoiceTemplates`
   * issue** The scrolling to top is not working.
   */

  useEffect(() => {
    window.scrollTo({ top: 0 });
    dispatch(invoiceTemplateActions.getInvoiceTemplates());
  }, [dispatch]);

  /**
   * Loads activities and credits when a different case is selected.
   *
   * @dependencies
   * - data
   *
   * @triggers
   * When `data` changes.
   *
   * issue 1: Here maybe we should set activitiesLoading to true?
   * When moving between cases, user may briefly see stale activities from previous case instead of a loading state
   *
   */
  useEffect(() => {
    setShowFutureSection(false);
    // I don't understand why we need to fetch credits here
    dispatch(customerActions.getCredits());
    if (data) {
      dispatch(caseActivityActions.getCaseActivities(data.id));
      // Also refresh the case so findings stay in sync with new activities (e.g. a license uploaded while away).
      dispatch(caseActions.getCaseWithChargeDetails(data.id, data.overview));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  /**
   * Fetches email templates when mail flow changes.
   *
   * Used by `CaseActivityCard` props for email activities; must update when the case switches flows.
   *
   * @dependencies
   * - data.mail_flow_id
   *
   * @triggers
   * When mail flow id changes.
   * Issue 1: Possible crash when data is temporarily undefined/null
   */
  useEffect(() => {
    data?.mail_flow_id
      ? templateService.getTemplates(data.mail_flow_id).then(
          (res) => {
            if (!res) res = [];
            setEmailTemplates(res);
          },
          (error) => {
            showSnackbarError(error);
          },
        )
      : setEmailTemplates([]);
  }, [data?.mail_flow_id, showSnackbarError]);

  /**
   * Decrements failure counter (scheduled or triggered) when a previously failed email succeeds on retry.
   * Clear retry flag
   * Show resume automation popup
   */
  useEffect(() => {
    const responseStatusSendMail = caseReducer.status.CASE_SEND_EMAIL;
    const responseStatusSaveMail = caseReducer.status.CASE_SAVE_EMAIL_TO_LOG;
    const sendOrSaveSucceeded =
      responseStatusSendMail === "success" ||
      responseStatusSaveMail === "success";

    if (sendOrSaveSucceeded && sentFailedEmailType) {
      dispatch(
        caseActions.decreaseFailed(
          data.id,
          sentFailedEmailType === caseConstants.CASEACTIVITY_TYPE_TRIGGERED
            ? "num_triggered_emails_failed"
            : "num_scheduled_emails_failed",
        ),
      );
      setSentFailedEmailType("");
      handleResumeAutomationPopup();
    } else if (
      responseStatusSendMail === "success" &&
      !caseReducer.trigger_sending_email_failed &&
      data?.num_scheduled_emails_paused > 0 &&
      data?.num_scheduled_emails_failed === 0 &&
      data?.num_triggered_emails_failed === 0
    ) {
      setResumeAutomationPopup(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    caseReducer.status.CASE_SEND_EMAIL,
    caseReducer.status.CASE_SAVE_EMAIL_TO_LOG,
  ]);

  /**
   * Shows success/error feedback after generating invoice.
   *
   * @triggers
   * When invoice generation status changes.
   *
   * @user_flow
   * 1. Generate invoice completes.
   * 2. Success: snackbar, close address dialog, `getCaseActivities`.
   * 3. Error: error snackbar, clear CASE_GENERATE_INVOICE state.
   */
  useEffect(() => {
    if (caseReducer.status.CASE_GENERATE_INVOICE === "success") {
      showSnackbarSuccess("Success!");
      setOpenAddressPopup(false);
      dispatch(caseActivityActions.getCaseActivities(data.id));
    } else if (catchError(caseReducer.status.CASE_GENERATE_INVOICE)) {
      showSnackbarError(caseReducer.status.CASE_GENERATE_INVOICE);
      dispatch(caseActivityActions.clearState("CASE_GENERATE_INVOICE"));
    }
  }, [
    dispatch,
    data.id,
    caseReducer.status.CASE_GENERATE_INVOICE,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  /**
   * Shows success/error feedback after updating opponent contact details.
   *
   * @triggers
   * When that status changes.
   *
   * @user_flow
   * 1. Save opponent details → status success or error.
   * 2. Show snackbar; clear Redux status message.
   */
  useEffect(() => {
    if (caseReducer.status.CASE_UPDATE_OPPONENT_DETAILS === "success") {
      showSnackbarSuccess("Success!");
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_OPPONENT_DETAILS"));
    } else if (catchError(caseReducer.status.CASE_UPDATE_OPPONENT_DETAILS)) {
      showSnackbarError(caseReducer.status.CASE_UPDATE_OPPONENT_DETAILS);
      dispatch(caseActions.clearStateMessage("CASE_UPDATE_OPPONENT_DETAILS"));
    }
  }, [
    dispatch,
    data.id,
    caseReducer.status.CASE_UPDATE_OPPONENT_DETAILS,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  /**
   * One-shot UI when `trigger_sending_email_failed` is set on `caseReducer` (email send failed outside normal status).
   *
   * Used to show a generic error and immediately `restoreTriggerEmailFailed` so the flag does not stick.
   *
   * @triggers
   * When trigger_sending_email_failed flag is set
   *
   * @user_flow
   * 1. Some send path sets `trigger_sending_email_failed`.
   * 2. Show error snackbar; dispatch restore to clear flag.
   */
  useEffect(() => {
    if (caseReducer.trigger_sending_email_failed) {
      showSnackbarError("Something went wrong. Email was not sent");
      dispatch(caseActions.restoreTriggerEmailFailed());
    }
  }, [caseReducer.trigger_sending_email_failed, dispatch, showSnackbarError]);

  const saveNote = () => {
    if (!noteInput || noteInput.length === 0) setAddNoteErrorActive(true);
    else {
      dispatch(
        caseActivityActions.saveNote(
          data.id,
          noteInput.replace(/[\r\n]+$/, ""),
        ),
      );
    }
  };

  // useCallback keeps a stable reference so child popups don't re-trigger their effects on every render (was causing a refetch loop).
  const triggerResumeAutomation = useCallback(() => {
    if (isAutomationPaused && !caseReducer.trigger_sending_email_failed) {
      setResumeAutomationPopup(true);
    }
  }, [isAutomationPaused, caseReducer.trigger_sending_email_failed]);

  const handleResumeAutomationPopup = () => {
    if (
      (data.num_scheduled_emails_failed === 1 ||
        data.num_triggered_emails_failed === 1) &&
      !(scheduledActivities.length === 1)
    ) {
      setResumeAutomationPopup(true);
    }
  };

  const activateAutomation = () => {
    dispatch(
      caseActions.toggleAutomation(
        data.id,
        caseConstants.CASE_AUTOMATION_STATE_SCHEDULED,
      ),
    );
    setResumeAutomationPopup(false);
  };

  /**
   * After a case note is saved successfully (`CASEACTIVITY_ADD`), reset the add-note dialog and Redux status.
   *
   * Used from the “add note” dialog in this section.
   *
   * @dependencies
   * - addNoteState (derived from caseActivityReducer.status.CASEACTIVITY_ADD), dispatch
   *
   * @triggers
   * When add succeeds.
   *
   * @user_flow
   * 1. `saveNote` completes with success.
   * 2. Clear input, close dialog, clear CASEACTIVITY_ADD.
   */
  useEffect(() => {
    if (addNoteState) {
      setNoteInput(null);
      setAddNoteDialogOpen(false);
      setAddNoteErrorActive(false);
      dispatch(caseActivityActions.getCaseActivities(data.id));
      dispatch(caseActivityActions.clearState("CASEACTIVITY_ADD"));
    }
  }, [addNoteState, data.id, dispatch]);

  /**
   * After a note is deleted (`CASEACTIVITY_DELETE` success), refetch activities and clear status.
   *
   * Used so the activity list immediately drops the removed note.
   *
   * @dependencies
   * - deleteNoteState, data.id, dispatch
   *
   * @triggers
   * When delete succeeds.
   *
   * @user_flow
   * 1. `deleteNote` completes.
   * 2. `getCaseActivities`; `clearState("CASEACTIVITY_DELETE")`.
   */
  useEffect(() => {
    if (deleteNoteState) {
      dispatch(caseActivityActions.getCaseActivities(data.id));
      dispatch(caseActivityActions.clearState("CASEACTIVITY_DELETE"));
    }
  }, [data.id, deleteNoteState, dispatch]);

  const deleteNote = (activity) => {
    dispatch(caseActivityActions.deleteNote(data.id, activity.id));
  };

  /**
   * Updates loading state and filters scheduled activities when activities load.
   *
   * @dependencies
   * - caseActivityReducer, activities, CASEACTIVITY_GET status
   *
   * @triggers
   * When reducer activities or GET status change.
   *
   * @user_flow
   * 1. Activities fetch completes → loading false.
   * 2. Filter scheduled future emails (not SENT) into `scheduledActivities`.
   * issue – Loader can stay forever on failed fetch, activitiesLoading is set to false only for "success".
   * If CASEACTIVITY_GET fails, users may see an endless spinner instead of an error/empty state
   */
  useEffect(() => {
    var state = caseActivityReducer.status.CASEACTIVITY_GET;
    if (state && state === "success") {
      setActivitiesLoading(false);
    }
    if (
      caseActivityReducer &&
      caseActivityReducer.activities &&
      caseActivityReducer.activities.length > 0
    ) {
      setScheduledActivities(
        caseActivityReducer.activities.filter(
          (x) =>
            x.content &&
            x.content.scheduled_at !== null &&
            x.content.scheduled_at !== undefined /**todo */ &&
            x.content.status !== caseConstants.CASE_ACTIVITY_STATE_SENT,
        ),
      );
    } else {
      setScheduledActivities([]);
    }
  }, [
    caseActivityReducer,
    caseActivityReducer.activities,
    caseActivityReducer.status.CASEACTIVITY_GET,
  ]);

  /**
   * Shows feedback and refreshes case after invoice cancel succeeds.
   */
  useEffect(() => {
    if (caseActivityReducer.status.INVOICE_CANCEL === "success") {
      showSnackbarSuccess("Success!");
      dispatch(caseActions.getCaseWithChargeDetails(data.id));
    } else if (catchError(caseActivityReducer.status.INVOICE_CANCEL)) {
      showSnackbarError(caseActivityReducer.status.INVOICE_CANCEL);
    }
    dispatch(caseActivityActions.clearState("INVOICE_CANCEL"));
    // eslint-disable-next-line
  }, [
    caseActivityReducer.status.INVOICE_CANCEL,
    dispatch,
    showSnackbarError,
    showSnackbarSuccess,
  ]);

  const isPriorityActivity = (x) =>
    x.priority === "HIGH" || x.priority === "LOW";

  // Priority activities (priority HIGH/LOW) bypass the category filter.
  const passesCategoryFilter = (activity) => {
    if (isPriorityActivity(activity)) return true;
    const category =
      activity.category || caseConstants.CASE_ACTIVITY_CATEGORY_OTHER;
    return !!enabledCategories[category];
  };

  const buildMainActivityCards = () => {
    // filter activites to exclude login with user_id , action_required letters without provider_id, scheduled with undefined schedule and the type is not included in the case constants
    // Some BE activity types are intentionally omitted from caseConstants and filtered out here (see case.constants.js).
    const filtered = caseActivityReducer.activities.filter(
      (x) =>
        (!x.content ||
          x.content.scheduled_at === undefined ||
          x.content.status === "FAILED" ||
          (x.type === "LOGIN" && !x.user_id)) &&
        !(
          x.content?.letter_status === "action_required" &&
          !x.content?.provider_id
        ) &&
        !(x.type === "LOGIN" && x.user_id) &&
        Object.values(caseConstants).includes(x.type),
    );

    const priorityActivities = filtered.filter(isPriorityActivity);
    const nonPriorityActivities = filtered.filter(
      (x) => !isPriorityActivity(x),
    );
    const normalActivities = nonPriorityActivities.filter(passesCategoryFilter);
    const totalNonPriority = nonPriorityActivities.length;

    const categoryCounts = nonPriorityActivities.reduce((counts, activity) => {
      counts[activity.category] = (counts[activity.category] || 0) + 1;
      return counts;
    }, {});

    const renderActivityCard = (x, index) => (
      <div
        key={x.id || "case_activity_card_number_" + index.toString()}
        className={styles.activity_container}
      >
        <CaseActivityCard
          deleteNote={deleteNote}
          activity={x}
          caseData={data}
          emailTemplates={emailTemplates}
          resumeAutomation={triggerResumeAutomation}
        />
      </div>
    );

    return {
      priorityItems: priorityActivities.map(renderActivityCard),
      normalItems: normalActivities.map(renderActivityCard),
      totalNonPriority,
      categoryCounts,
    };
  };

  const onPauseToggleClicked = () => {
    dispatch(
      caseActions.toggleAutomation(
        data.id,
        isAutomationPaused
          ? caseConstants.CASE_AUTOMATION_STATE_SCHEDULED
          : caseConstants.CASE_AUTOMATION_STATE_PAUSED,
      ),
    );
  };

  const visibleScheduledActivities = scheduledActivities
    .filter((a) => a.content.status !== "FAILED")
    .filter(passesCategoryFilter);

  const renderFutureList = () => {
    let items = visibleScheduledActivities
      .slice()
      .sort((x1, x2) => {
        const date1 = new Date(x1.content.scheduled_at);
        const date2 = new Date(x2.content.scheduled_at);
        return date2 - date1;
      })
      .map((x, index) => {
        return (
          <div key={x.id || index} className={styles.activity_container}>
            <CaseActivityCard
              deleteNote={deleteNote}
              activity={x}
              caseData={data}
              resumeAutomation={() => setSentFailedEmailType(x.content.type)}
              dottedborder
            ></CaseActivityCard>
          </div>
        );
      });
    if (items && items.length >= 2 && showFutureSection === false) {
      const hiddenCount = visibleScheduledActivities.length - 1;
      items = items.slice(items.length - 1);
      items.unshift(
        <div
          key={"__unique_show_future_id__"}
          className={styles.activity_container_more_button}
        >
          <Button
            variant="outlined"
            onClick={() => setShowFutureSection(true)}
            className={styles.add_note_button}
          >
            Show {hiddenCount} more Scheduled Email
            {hiddenCount === 1 ? "" : "s"}
          </Button>
        </div>,
      );
    }
    return items;
  };

  const handleOpenAddressPopup = () => {
    if (
      caseActivityReducer.activities.some(
        (activity) =>
          activity.type === caseConstants.CASEACTIVITY_TYPE_INVOICE_GENERATED &&
          activity.content?.type === "ACTIVE",
      )
    ) {
      setInfoPopupText(
        "An active Invoice already exists. Please cancel it before generating a new invoice.",
      );
      setInfoPopupTitle("Cannot generate an Invoice");
      setOpenInfoPopup(true);
      return;
    } else if (!caseReducer.case[data.id]?.invoice_template_id) {
      setInfoPopupText(
        "No invoice template set for this case. Pleae assign an invoice template before generating an invoice",
      );
      setInfoPopupTitle("Cannot generate an Invoice");
      setOpenInfoPopup(true);
      return;
    } else setOpenAddressPopup(true);
  };

  return (
    <div className={styles.root}>
      <AppSnackbar {...snackbarProps} />
      {data.callback_request && (
        <CallbackLogItem data={data.callback_request} caseId={data.id} />
      )}
      <hr
        style={{
          backgroundColor: "var(--color-theme-cloud)",
          marginBottom: "15px",
        }}
      ></hr>
      <div className={styles.title_container}>
        <div className={styles.title_font}>Case Activities</div>
        <div className={styles.title_right_side}>
          <div className={styles.flex_right}>
            {scheduledActivities.length > 0 &&
            !(
              scheduledActivities.length === 1 &&
              scheduledActivities[0].content?.status ===
                caseConstants.CASE_ACTIVITY_STATE_FAILED
            ) ? (
              <FormControlLabel
                labelPlacement="start"
                disabled={data && data.num_scheduled_emails_failed > 0}
                label={
                  isAutomationPaused
                    ? "Automation is paused"
                    : "Automation is active"
                }
                control={
                  <div>
                    <Tooltip
                      title={
                        data &&
                        (data.num_scheduled_emails_failed > 0 ||
                          data.num_triggered_emails_failed > 0)
                          ? "Resolve failed emails before resuming"
                          : ""
                      }
                      placement="top"
                      arrow
                    >
                      <span>
                        <Button
                          style={{
                            marginLeft: "20px",
                            marginRight: "11px",
                            width: "101px",
                            color: "var(--color-font-10) !important",
                            borderColor: "var(--color-font-10) !important",
                            position: "relative",
                            zIndex: 99,
                          }}
                          disabled={
                            data &&
                            (data.num_scheduled_emails_failed > 0 ||
                              data.num_triggered_emails_failed > 0)
                          }
                          className={styles.add_note_button}
                          variant="outlined"
                          onClick={onPauseToggleClicked}
                        >
                          {isAutomationPaused ? "Resume" : "Pause"}
                        </Button>
                      </span>
                    </Tooltip>
                  </div>
                }
              ></FormControlLabel>
            ) : (
              <></>
            )}
          </div>
          <div>
            {data.state !== "STANDBY" && data.state !== "CANCELLED" && (
              <Button
                variant="outlined"
                style={{ marginRight: "25px" }}
                onClick={handleOpenAddressPopup}
                className={styles.add_note_button}
              >
                GENERATE INVOICE
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => setAddNoteDialogOpen(true)}
              className={styles.add_note_button}
            >
              ADD NOTE
            </Button>
          </div>
        </div>
      </div>
      <div className={styles.activity_list_container}>
        {activitiesLoading ? (
          <div className={styles.loading}>
            <CircularProgress style={{ color: "black" }} />
          </div>
        ) : (
          (() => {
            const {
              priorityItems,
              normalItems,
              totalNonPriority,
              categoryCounts,
            } = buildMainActivityCards();
            const totalNonPriorityScheduled = scheduledActivities.filter(
              (a) => a.content.status !== "FAILED" && !isPriorityActivity(a),
            ).length;
            const hasScheduled = visibleScheduledActivities.length > 0;
            const showFilterRow =
              totalNonPriority + totalNonPriorityScheduled > 0;
            const showOtherActivitiesDivider =
              normalItems.length > 0 &&
              (priorityItems.length > 0 || hasScheduled);
            const noFilteredItemsVisible =
              showFilterRow && normalItems.length === 0 && !hasScheduled;

            return (
              <>
                {priorityItems}
                {showFilterRow && (
                  <CaseActivityFilter
                    enabledCategories={enabledCategories}
                    onToggleCategory={toggleCategory}
                    categoryCounts={categoryCounts}
                  />
                )}
                {hasScheduled && (
                  <div
                    className={
                      priorityItems.length > 0
                        ? `${styles.future_section_visible} ${styles.after_priority_gap}`
                        : styles.future_section_visible
                    }
                  >
                    {renderFutureList()}
                  </div>
                )}
                {showOtherActivitiesDivider ? (
                  <div className={styles.priority_divider_container}>
                    <div className={styles.priority_divider_border} />
                    <span className={styles.priority_divider_content}>Now</span>
                    <div className={styles.priority_divider_border} />
                  </div>
                ) : null}
                {normalItems}
                {noFilteredItemsVisible && (
                  <div className={styles.filter_empty_message}>
                    No activities match the selected filters.
                  </div>
                )}
              </>
            );
          })()
        )}
      </div>
      <AddressPopup
        open={openAddressPopup}
        onClose={() => setOpenAddressPopup(false)}
        case={data}
      />
      <DialogPopup
        title={"Add Note"}
        open={addNoteDialogOpen}
        onClose={() => setAddNoteDialogOpen(false)}
      >
        <TextfieldInput
          label="Note"
          placeholder="Type Note here"
          onChange={(event) => {
            if (event) {
              setAddNoteErrorActive(false);
              setNoteInput(event.target.value);
            }
          }}
          multiline
          maxRows={6}
          rows={6}
          name={"add_note_input"}
          value={noteInput}
          error={addNoteErrorActive}
        ></TextfieldInput>
        <DialogActions>
          <div className="action_container">
            <Button
              onClick={() => {
                setNoteInput(null);
                setAddNoteDialogOpen(false);
                setAddNoteErrorActive(false);
              }}
              className="btn_cancel"
            >
              Cancel
            </Button>
            <Button onClick={saveNote} autoFocus className="btn_confirm">
              Save
            </Button>
          </div>
        </DialogActions>
      </DialogPopup>
      <AreYouSurePopup
        open={resumeAutomationPopup}
        close={() => setResumeAutomationPopup(false)}
        onClick={activateAutomation}
      >
        The email automation is currently paused. Do you want to resume it?
      </AreYouSurePopup>
      <InfoPopup
        open={openInfoPopup}
        close={() => setOpenInfoPopup(false)}
        title={infoPopupTitle}
      >
        <p>{infoPopupText}</p>
      </InfoPopup>
    </div>
  );
}

export default CaseActivitySection;
